import { canadaProvinces, us_states } from "./geo";
import _ from "lodash";

export const CHART_COLORS = [
  0x5788d3, 0xffc54d, 0x796cc8, 0xee6644, 0x57ccd3, 0x57d38e, 0xe2619d,
  0xadd235, 0x1f8775, 0x3c598d, 0xe5aa1a, 0x44ace6, 0xbc4645, 0x1dc2f5,
  0x969add, 0xa984df, 0xfac0ec, 0x884186, 0xecf730, 0x2ca6b8
];

export const processBarChartData = (
  rawData: any[],
  group_by: string,
  compare_by: string,
  metric: string,
  filterCompareBy = false
) => {
  let chartData: any[] = [];

  rawData = formatGroupingName(rawData);
  if (compare_by && group_by) {
    chartData = generateGroupCompareByData(
      rawData,
      metric,
      group_by,
      compare_by,
      filterCompareBy
    );
  } else if (group_by) {
    chartData = formatDataValues(rawData, metric, group_by, true);
  }

  if (["dma", "fsa"].includes(group_by))
    chartData = chartData.filter((entry: any) => entry[group_by] != "Unknown");

  return chartData;
};

export const processLineChartData = (
  rawData: any[],
  group_by: string,
  compare_by: string,
  metric: string
) => {
  let chartData: any[] = [];

  rawData = formatGroupingName(rawData);
  if (compare_by && group_by) {
    chartData = generateGroupCompareByData(
      rawData,
      metric,
      group_by,
      compare_by
    );
  } else if (group_by) {
    chartData = formatDataValues(rawData, metric, group_by);
  }
  return chartData;
};

export const processPieChartData = (
  rawData: any[],
  group_by: string,
  metric: string
) => {
  let chartData: any[] = [];

  rawData = formatGroupingName(rawData);
  if (group_by) {
    chartData = formatDataValues(rawData, metric, group_by, true);
  }

  // Filter out null and zero entries
  chartData = chartData.filter((entry: any) => entry[metric]);

  if (["dma", "fsa"].includes(group_by))
    chartData = chartData.filter((entry: any) => entry[group_by] != "Unknown");

  return chartData;
};

// Returns a new array of data with a key that corresponds to the number of days
// since the launch of a publication instead of the actual date. Assumes arrays are
// already sorted by date.
export const formatDataForDaysSinceLaunch = (
  data: any[],
  newGroupByKey: string
) => {
  let formatedData: any[] = [];

  // Group data on a per publication basis
  const groupedData = _.groupBy(data, (entry: any) => entry["publication"]);

  // For each publication, map the data and convert date to 'day since launch'.
  _.map(groupedData, (array: any[]) => {
    array.some((entry: any, index: number) => {
      entry[newGroupByKey] = index + 1;
      formatedData.push(entry);
      return index >= 13; // Limit the number of days to display to 14
    });
  });

  // Limit amount of data to two weeks (14 days)

  return formatedData;
};

const generateGroupCompareByData = (
  data: any[],
  metric: string,
  group_by: string,
  compare_by: string,
  sort: boolean = false
) => {
  let group_bys: any = [];
  let chartData: any = [];

  //identify all the available group_by values
  group_bys = data
    .map((entry: any) => entry[group_by])
    .filter(
      (entry: any, index: number, arr: any[]) => arr.indexOf(entry) == index
    );

  //create object for each grouping
  group_bys.forEach((group: string) => {
    let dataGroup = data.filter((entry) => entry[group_by] == group);
    let dataEntry: any = {};

    //merging rows to one group
    dataGroup.forEach((entry: any) => {
      let value_key = `${entry[compare_by]} ${metric.split("_").join(" ")}`;
      dataEntry[value_key] = entry[metric];
      dataEntry[group_by] =
        (entry[group_by] || []).length == 0 ? "Unknown" : entry[group_by];
    });
    chartData.push(dataEntry);
  });

  if (sort) {
    // Get all possible compare by keys
    let keys = new Set();
    chartData.forEach((entry: any) =>
      _.keys(entry).forEach((key: any) => keys.add(key))
    );
    keys.delete(group_by);

    // Get the total for each key
    let totals: any = {};
    keys.forEach((key: string) => {
      totals[key] = chartData.reduce((sum: number, entry: any) => {
        return sum + entry[key];
      }, 0);
    });

    // Sort and return data for the top 5 compare_by keys
    const sortedTotals = _.sortBy(_.toPairs(totals), [
      (a: any[]) => a[1]
    ]).reverse();

    const topFive = sortedTotals.map((pair: any[]) => pair[0]).slice(0, 5);
    chartData = chartData.map((entry: any) =>
      _.pick(entry, [...topFive, group_by])
    );

    //sort groups
    chartData = _.sortBy(chartData, (entry) => {
      let totalVal = _.values(_.omit(entry, [group_by])).reduce(
        (acc: number, sum: number) => acc + sum,
        0
      );
      return totalVal;
    }).reverse();
    chartData = _.partition(chartData, (entry: any) =>
      _.values(_.omit(entry, [group_by])).reduce(
        (acc: number, sum: number) => acc + sum,
        0
      )
    );
    chartData = _.flatten(chartData);

    //sort compare bys
    chartData = chartData.map((entry: any) => {
      let sortedComparisons = _.sortBy(
        _.toPairs(_.omit(entry, [group_by])),
        (entry) => {
          return entry[1];
        }
      ).reverse();
      //moving nulls to end
      sortedComparisons = _.flatten(
        _.partition(sortedComparisons, (entry: any) => entry[1] != null)
      );
      let topFiveComparisons = _.fromPairs(sortedComparisons.slice(0, 5));
      return { ...topFiveComparisons, [group_by]: entry[group_by] };
    });
  }
  return chartData;
};

const formatDataValues = (
  data: any[],
  metric: string,
  group_by: string,
  sort = false
) => {
  let chartData = data.map((entry: any) => {
    let dataEntry: any = {};

    dataEntry[metric] =
      typeof entry[metric] == "string"
        ? parseInt(entry[metric], 10)
        : entry[metric];
    if (entry[group_by])
      dataEntry[group_by] =
        entry[group_by].length == 0 ? "Unknown" : entry[group_by];
    else dataEntry[group_by] = "Unknown";

    return dataEntry;
  });
  if (sort) {
    chartData = _.sortBy(chartData, (entry) => {
      return entry[metric];
    }).reverse();
    chartData = _.partition(chartData, (entry) => {
      return entry[metric];
    });
    chartData = _.flatten(chartData);
    chartData = _.values(chartData);
  }

  return chartData;
};

const getRegion = (region: Object) => (r: { state: string }) =>
  Object.keys(region).includes(r.state);

const calculateRegionData = (
  data: Array<any>,
  metric: string,
  statesToCount: any,
  extraStates: any
) => {
  const { sum, values } = data.filter(getRegion(statesToCount)).reduce(
    (acc: any, el: any) => {
      acc["sum"] += el[metric];
      acc["values"][statesToCount[el.state]] = el[metric];
      return acc;
    },
    { sum: 0, values: {} }
  );

  const extraValues = data
    .filter(getRegion(extraStates))
    .reduce((acc: any, curr: any) => (acc += curr[metric]), 0);

  return { extraValues, sum, values };
};

export const processMapData = (
  data: any,
  metric: string,
  isUSBased: boolean
) => {
  let val = {} as any;
  let total = 0;
  let zeroData = false;
  let extraStates = 0 as any;

  if (!isUSBased) {
    const { sum, values, extraValues } = calculateRegionData(
      data,
      metric,
      canadaProvinces,
      us_states
    );
    total = sum;
    val = values;
    extraStates = extraValues;
  }

  if (isUSBased) {
    const { sum, values, extraValues } = calculateRegionData(
      data,
      metric,
      us_states,
      canadaProvinces
    );
    total = sum;
    val = values;
    extraStates = extraValues;
  } else if (data.length > 100) {
    data.forEach((el: any) => {
      total += el[metric];
      val[el["fsa"]] = el[metric];
    });
  }

  zeroData = total == 0;
  let max = Infinity * -1;
  let min = Infinity;
  data.forEach((i: any) => {
    if (i[metric] > max) max = i[metric];

    if (i[metric] < min) min = i[metric];
  });

  return { val, max, min, zeroData, extraStates };
};

export const calculateLineTicks = (
  data: any[],
  metric: string,
  group_by: string,
  compare_by: string = null
) => {
  let tickValues: number[] = [];
  let max, min, interval;
  if (data && data.length != 0) {
    let values: number[] = [];

    data.forEach((entry: any) => {
      let keys = Object.keys(entry).filter((key: string) => {
        return key != group_by;
      });
      keys.forEach((key: string) => {
        values.push(entry[key]);
      });
    });

    max = values.reduce((a, b) => Math.min(a, b), 0);
    min = values.reduce((a, b) => Math.max(a, b), 0);
    interval = (max - min) / 3;
    tickValues = [min, min + interval, min + 2 * interval, max];
  }
  return tickValues;
};

export const formatGroupingName = (data: any[]) => {
  //format data to conform to design docs
  let processedData = data.map((item: any) => {
    if (item.channel == "distribution" || item.channel == "reebee") {
      item.channel = "flipp network";
    }
    return item;
  });
  return processedData;
};



// WEBPACK FOOTER //
// ./client/modules/core/Chart/common/utils.tsx